import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { money, moneyCompact, num, fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { HardHat, Search, ArrowRight, ChevronDown, CheckCircle2, Circle, Trash2, Download, UserPlus, Tag } from "lucide-react";
import { useRowSelection } from "@/hooks/useRowSelection";
import { BulkBar, BulkBtn, RowCheck } from "@/components/BulkBar";
import { ReassignDialog } from "@/components/ReassignDialog";
import { BidStatusDialog } from "@/components/BidStatusDialog";

const DEFAULT_MILESTONES = ["Mobilization", "Materials on site", "Construction start", "Substantial completion", "Punch & closeout"];
const initMilestones = (bid) => (bid.ops?.milestones?.length ? bid.ops.milestones : DEFAULT_MILESTONES.map((label) => ({ label, done: false })));

function OpsPanel({ bid, onSaved }) {
  const [milestones, setMilestones] = useState(initMilestones(bid));
  const [notes, setNotes] = useState(bid.ops?.pm_notes || "");
  const [saving, setSaving] = useState(false);
  const toggle = (i) => setMilestones((m) => m.map((x, idx) => (idx === i ? { ...x, done: !x.done } : x)));
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/bids/${bid.bid_id}/ops`, { milestones, pm_notes: notes });
      toast.success("Saved"); onSaved?.(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };
  return (
    <div className="border-t border-slate-100 px-4 py-4 bg-slate-50/50" data-testid={`ops-panel-${bid.bid_id}`}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Field milestones</div>
          <div className="space-y-1.5">
            {milestones.map((m, i) => (
              <button key={i} data-testid={`milestone-${bid.bid_id}-${i}`} onClick={() => toggle(i)}
                className="flex items-center gap-2 text-sm w-full text-left">
                {m.done ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Circle className="w-4 h-4 text-slate-300" />}
                <span className={m.done ? "line-through text-slate-400" : "text-slate-700"}>{m.label}</span>
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Hand-off notes</div>
          <Textarea data-testid={`ops-notes-${bid.bid_id}`} rows={5} value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Site access, key contacts, long-lead items, anything the field needs." />
        </div>
      </div>
      <div className="mt-3 flex justify-end">
        <Button size="sm" data-testid={`ops-save-${bid.bid_id}`} onClick={save} disabled={saving}
          className="bg-[#003E87] hover:bg-[#00305F] text-white">Save hand-off</Button>
      </div>
    </div>
  );
}

export default function Awarded() {
  const navigate = useNavigate();
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);
  const sel = useRowSelection();
  const [reassign, setReassign] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);

  const doStatus = async (status) => {
    try {
      const res = await api.post("/bids/bulk", { action: "status", bid_ids: sel.ids, status });
      toast.success(`Moved ${res.data.updated} project${res.data.updated === 1 ? "" : "s"} to ${status}`);
      setStatusOpen(false); sel.clear(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not change status"); }
  };

  const load = () => api.get("/bids", { params: { status: "won" } }).then((r) => setBids(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const s = q.trim().toLowerCase();
  const rows = s ? bids.filter((b) => (b.name || "").toLowerCase().includes(s) || (b.client_name || "").toLowerCase().includes(s)) : bids;
  const orderedIds = rows.map((b) => b.bid_id);
  const allSelected = rows.length > 0 && sel.count === rows.length;
  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${sel.count} project${sel.count === 1 ? "" : "s"}? This is permanent.`)) return;
    try {
      const res = await api.post("/bids/bulk", { action: "delete", bid_ids: sel.ids });
      toast.success(`Deleted ${res.data.deleted} project${res.data.deleted === 1 ? "" : "s"}`);
      setBids((list) => list.filter((x) => !sel.ids.includes(x.bid_id)));
      sel.clear();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not delete"); }
  };
  const doReassign = async (uid) => {
    try {
      const res = await api.post("/bids/bulk", { action: "reassign", bid_ids: sel.ids, assignee_id: uid });
      toast.success(`Reassigned ${res.data.updated} project${res.data.updated === 1 ? "" : "s"}`);
      setReassign(false); sel.clear(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Reassign failed"); }
  };
  const totalValue = bids.reduce((a, b) => a + (b.stored_total || 0), 0);
  const patch = (nb) => setBids((list) => list.map((x) => (x.bid_id === nb.bid_id ? nb : x)));
  const remove = async (b, e) => {
    e.stopPropagation();
    const reason = window.prompt(`Delete "${b.name}"? It leaves the board and every list; only an admin record of who, when and why is kept.\n\nWhy is it being deleted?`);
    if (reason === null) return;
    try {
      await api.delete(`/bids/${b.bid_id}`, { data: { reason } });
      setBids((list) => list.filter((x) => x.bid_id !== b.bid_id));
      toast.success("Bid deleted");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not delete"); }
  };

  return (
    <div className="px-6 sm:px-8 py-8 pb-24">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-black flex items-center gap-2">
            <HardHat className="w-5 h-5 text-[#003E87]" /> Awarded projects
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Work you've won. Track the field hand-off — milestones and notes — right here.</p>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right"><div className="font-display text-xl font-bold text-black tabular-nums">{bids.length}</div><div className="text-[10px] uppercase tracking-wider text-slate-400">Won</div></div>
          <div className="text-right"><div className="font-display text-xl font-bold text-black tabular-nums">{moneyCompact(totalValue)}</div><div className="text-[10px] uppercase tracking-wider text-slate-400">Value</div></div>
          <div className="relative w-56 max-w-full">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="awarded-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a project…" className="pl-9" />
          </div>
        </div>
      </div>

      {rows.length > 0 && (
        <div data-testid="awarded-select-all" role="button" tabIndex={0} onClick={() => sel.selectAll(orderedIds)} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 mb-3 cursor-pointer">
          <RowCheck checked={allSelected} onClick={() => {}} testid="awarded-select-all-check" />
          {allSelected ? "Clear all" : "Select all"}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-400 py-16 text-center">Loading…</div>
      ) : rows.length === 0 ? (
        <Card className="p-12 text-center text-sm text-slate-500">No awarded projects yet. Mark a submitted bid "Won" and it lands here.</Card>
      ) : (
        <div className="space-y-2">
          {rows.map((b, i) => (
            <Card key={b.bid_id} data-testid={`awarded-row-${b.bid_id}`} className={`overflow-hidden ${sel.isSelected(b.bid_id) ? "border-[#003E87]" : ""}`}>
              <div className="flex items-center gap-4 p-4">
                <RowCheck checked={sel.isSelected(b.bid_id)} testid={`awarded-check-${b.bid_id}`}
                  onClick={(e) => sel.toggle(b.bid_id, i, e.shiftKey, orderedIds)} />
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 shrink-0">Won</span>
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/bids/${b.bid_id}`)}>
                  <div className="text-sm font-medium text-black truncate">{b.name}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {b.client_name || "No client"} · {num(b.capacity_mw)} MW{b.state ? ` · ${b.state}` : ""}
                    {b.decided_at ? ` · won ${fmtDate(b.decided_at)}` : ""}
                  </div>
                </div>
                <div className="text-sm font-mono font-medium text-slate-700 shrink-0">{b.stored_total ? money(b.stored_total) : "—"}</div>
                <button data-testid={`awarded-expand-${b.bid_id}`} onClick={() => setOpenId(openId === b.bid_id ? null : b.bid_id)}
                  className="shrink-0 text-slate-400 hover:text-[#003E87] flex items-center gap-1 text-xs">
                  Hand-off <ChevronDown className={`w-4 h-4 transition ${openId === b.bid_id ? "rotate-180" : ""}`} />
                </button>
                <button data-testid={`awarded-delete-${b.bid_id}`} onClick={(e) => remove(b, e)} title="Delete bid"
                  className="shrink-0 text-slate-300 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                <ArrowRight className="w-4 h-4 text-slate-300 shrink-0 cursor-pointer" onClick={() => navigate(`/bids/${b.bid_id}`)} />
              </div>
              {openId === b.bid_id && <OpsPanel bid={b} onSaved={patch} />}
            </Card>
          ))}
        </div>
      )}

      <BulkBar count={sel.count} onClear={sel.clear}>
        <BulkBtn testid="awarded-bulk-status" icon={Tag} onClick={() => setStatusOpen(true)}>Set status</BulkBtn>
        <BulkBtn testid="awarded-bulk-reassign" icon={UserPlus} onClick={() => setReassign(true)}>Reassign</BulkBtn>
        <BulkBtn testid="awarded-bulk-export" icon={Download} onClick={async () => {
          try {
            const res = await api.post("/bids/export", { bid_ids: sel.ids }, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a"); a.href = url; a.download = "estimate-awarded.xlsx"; a.click();
            URL.revokeObjectURL(url);
          } catch { toast.error("Export failed"); }
        }}>Export</BulkBtn>
        <BulkBtn testid="awarded-bulk-delete" icon={Trash2} tone="danger" onClick={bulkDelete}>Delete</BulkBtn>
      </BulkBar>
      <ReassignDialog open={reassign} onOpenChange={setReassign} count={sel.count} onConfirm={doReassign} />
      <BidStatusDialog open={statusOpen} onOpenChange={setStatusOpen} count={sel.count} onConfirm={doStatus} />
    </div>
  );
}
